import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { MessageBanner } from "@folio/stripes/components";
import { libraryEdit } from "../../types/routePaths";
import { placeholderToken, resolveVariantStatus, splitByEmptyPlaceholders, } from "../../utils/messageTemplateWarnings";
import styles from "./MessageTemplateWarnings.module.css";
var WARNING_MESSAGE_IDS = {
    CONTACT_MISSING: "ui-br-legaldeposits.libraries.messageTemplates.warning.CONTACT_MISSING",
    REFERENT_SIGNATURE_DISABLED: "ui-br-legaldeposits.libraries.messageTemplates.warning.REFERENT_SIGNATURE_DISABLED",
};
var SETTING_FIELD_LABEL_IDS = {
    CONTACT_MISSING: "ui-br-legaldeposits.libraries.field.correspondenceContact",
    REFERENT_SIGNATURE_DISABLED: "ui-br-legaldeposits.libraries.field.includeReferentInSignature",
};
export var EMPTY_CHIP_HINT_IDS = {
    CONTACT_MISSING: "ui-br-legaldeposits.libraries.messageTemplates.placeholders.empty.CONTACT_MISSING",
    REFERENT_SIGNATURE_DISABLED: "ui-br-legaldeposits.libraries.messageTemplates.placeholders.empty.REFERENT_SIGNATURE_DISABLED",
};
export var EmptyPlaceholderBanners = function (_a) {
    var libraryId = _a.libraryId, usedEmptyPlaceholders = _a.usedEmptyPlaceholders, onOpenLibraryEdit = _a.onOpenLibraryEdit;
    var formatMessage = useIntl().formatMessage;
    var handleLinkClick = useCallback(function (e) {
        // a modified click keeps the browser's own "open in a new tab"
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
            return;
        e.preventDefault();
        onOpenLibraryEdit();
    }, [onOpenLibraryEdit]);
    return (React.createElement(React.Fragment, null, usedEmptyPlaceholders.map(function (_a) {
        var placeholder = _a.placeholder, reason = _a.reason;
        var settingsPath = [
            formatMessage({ id: "ui-br-legaldeposits.navigation.libraries" }),
            formatMessage({
                id: "ui-br-legaldeposits.libraries.messageTemplates.warning.path.detail",
            }),
            formatMessage({ id: "ui-br-legaldeposits.action.menu.label" }),
            formatMessage({
                id: "ui-br-legaldeposits.libraries.detail.action.edit",
            }),
            formatMessage({ id: SETTING_FIELD_LABEL_IDS[reason] }),
        ].join(" › ");
        return (React.createElement(MessageBanner, { key: placeholder, type: "warning", className: styles.banner },
            React.createElement("div", { "data-empty-placeholder-warning": placeholder },
                React.createElement("p", { className: styles.line }, formatMessage({ id: WARNING_MESSAGE_IDS[reason] }, { token: placeholderToken(placeholder) })),
                React.createElement("p", { className: styles.line },
                    formatMessage({
                        id: "ui-br-legaldeposits.libraries.messageTemplates.warning.where",
                    }, { path: settingsPath }),
                    " ",
                    React.createElement(Link, { to: libraryEdit(libraryId), onClick: handleLinkClick }, formatMessage({
                        id: "ui-br-legaldeposits.libraries.messageTemplates.warning.openLibraryEdit",
                    }))))));
    })));
};
export var OnlyCustomVariantBanner = function (_a) {
    var customVariant = _a.customVariant, singleLabel = _a.singleLabel, pluralLabel = _a.pluralLabel, isPeriodicalType = _a.isPeriodicalType;
    var formatMessage = useIntl().formatMessage;
    var _b = customVariant === "SINGLE"
        ? [singleLabel, pluralLabel]
        : [pluralLabel, singleLabel], customLabel = _b[0], systemLabel = _b[1];
    return (React.createElement(MessageBanner, { type: "warning", className: styles.banner },
        React.createElement("div", { "data-only-custom-variant": customVariant }, formatMessage({
            id: isPeriodicalType
                ? "ui-br-legaldeposits.libraries.messageTemplates.warning.onlyOneVariantCustom.issue"
                : "ui-br-legaldeposits.libraries.messageTemplates.warning.onlyOneVariantCustom.title",
        }, { customLabel: customLabel, systemLabel: systemLabel }))));
};
export var VariantInfoNote = function (_a) {
    var singleLabel = _a.singleLabel, pluralLabel = _a.pluralLabel, isPeriodicalType = _a.isPeriodicalType;
    var formatMessage = useIntl().formatMessage;
    return (React.createElement(MessageBanner, { className: styles.banner },
        React.createElement("div", { "data-variant-info": true }, formatMessage({
            id: isPeriodicalType
                ? "ui-br-legaldeposits.libraries.messageTemplates.variantInfo.issue"
                : "ui-br-legaldeposits.libraries.messageTemplates.variantInfo.title",
        }, { singleLabel: singleLabel, pluralLabel: pluralLabel }))));
};
export var VariantAccordionLabel = function (_a) {
    var label = _a.label, variant = _a.variant, source = _a.source, updatedAt = _a.updatedAt;
    var formatMessage = useIntl().formatMessage;
    var status = resolveVariantStatus(source, updatedAt);
    if (!status)
        return React.createElement(React.Fragment, null, label);
    var statusText = status.kind === "SYSTEM"
        ? formatMessage({
            id: "ui-br-legaldeposits.libraries.messageTemplates.variantStatus.system",
        })
        : status.modifiedOn
            ? formatMessage({
                id: "ui-br-legaldeposits.libraries.messageTemplates.variantStatus.library",
            }, { date: status.modifiedOn })
            : formatMessage({
                id: "ui-br-legaldeposits.libraries.messageTemplates.variantStatus.libraryWithoutDate",
            });
    return (React.createElement("span", null,
        label,
        React.createElement("span", { className: status.kind === "SYSTEM"
                ? styles.variantStatus
                : "".concat(styles.variantStatus, " ").concat(styles.variantStatusCustom), "data-variant-status": variant }, " \u00B7 ".concat(statusText))));
};
export var HighlightedPreviewSubject = function (_a) {
    var subject = _a.subject, emptyPlaceholders = _a.emptyPlaceholders, className = _a.className, markClassName = _a.markClassName;
    var segments = useMemo(function () {
        return splitByEmptyPlaceholders(subject, emptyPlaceholders.map(function (_a) {
            var placeholder = _a.placeholder;
            return placeholder;
        }));
    }, [subject, emptyPlaceholders]);
    return (React.createElement("div", { className: className }, segments.map(function (segment) {
        return segment.emptyPlaceholder ? (React.createElement("mark", { key: segment.start, className: markClassName, "data-empty-placeholder": segment.emptyPlaceholder }, segment.text)) : (React.createElement("span", { key: segment.start }, segment.text));
    })));
};
export var EmptyPlaceholdersPreviewNote = function (_a) {
    var emptyPlaceholders = _a.emptyPlaceholders;
    var formatMessage = useIntl().formatMessage;
    var referentUnknown = emptyPlaceholders.some(function (_a) {
        var reason = _a.reason;
        return reason === "REFERENT_UNKNOWN";
    });
    return (React.createElement("div", { className: styles.previewNote, "data-empty-placeholders-note": true },
        React.createElement("p", { className: styles.line }, formatMessage({
            id: "ui-br-legaldeposits.libraries.messageTemplates.preview.emptyPlaceholdersNote",
        })),
        referentUnknown && (React.createElement("p", { className: styles.line, "data-referent-unknown-note": true }, formatMessage({
            id: "ui-br-legaldeposits.libraries.messageTemplates.preview.referentUnknownNote",
        }, { token: placeholderToken("REFERENT_SIGNATURE") })))));
};
